/**
 * GoodShowHQ first-run checklist.
 *
 * Translated from the Tailwind + lucide-react reference at
 * `design/pages/marketing/Welcome.jsx` into MUI 7 + @mui/icons-material.
 * The reference file is the source of truth — when this file and
 * the reference disagree, the reference wins.
 *
 * Auth-required route: /welcome. Reachable from the post-auth
 * router on brand-new accounts, and from a future Settings entry
 * for "Re-run welcome." Unauthenticated visits redirect to /signin
 * via the RequireAuth wrapper at the route level.
 *
 * State persistence: server-side `users/{uid}.welcomeState` via
 * GraphQL. Each "Done" tap calls `markWelcomeStep`. The "Skip"
 * link calls `skipWelcome`. On mount the page hydrates from
 * `getCurrentUser.welcomeState` so a parent who reloaded mid-flow
 * picks up where they left off.
 *
 * Auto-detection (steps 3 + 4) — not in this commit:
 *   - Desktop installed: when the Desktop app first connects to
 *     Firebase under the user's account, it writes a
 *     `desktopFirstSeenAt` field on the user doc. The Welcome page
 *     would subscribe and auto-mark step 3 done when it appears.
 *   - Kid signed in: when the Desktop reports its first
 *     YouTube-signed-in profile, it writes `firstProfileSyncedAt`
 *     on the user doc. Same subscription pattern.
 * For first ship, both are self-attested via "Done" buttons.
 * (TODO: subscribe to desktopFirstSeenAt → auto-mark step 3.)
 * (TODO: subscribe to firstProfileSyncedAt → auto-mark step 4.)
 */
import * as React from 'react';
import {useState, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import MenuBookRounded from '@mui/icons-material/MenuBookRounded';
import PeopleRounded from '@mui/icons-material/PeopleRounded';
import DesktopWindowsRounded from '@mui/icons-material/DesktopWindowsRounded';
import YouTubeRounded from '@mui/icons-material/YouTube';
import AppleRounded from '@mui/icons-material/Apple';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import logoMark from '../../assets/brand/logo-mark.svg';
import {
  useGetWelcomeBundle,
  useMarkWelcomeStep,
  useSkipWelcome,
  WelcomeState,
  WelcomeStep,
} from '../../api/graphql_welcome';

const C = {
  slate50: '#F8FAFC',
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',
  slate300: '#CBD5E1',
  slate400: '#94A3B8',
  slate500: '#64748B',
  slate600: '#475569',
  slate700: '#334155',
  slate800: '#1E293B',
  slate900: '#0F172A',
  indigo50: '#EEF2FF',
  indigo100: '#E0E7FF',
  indigo300: '#A5B4FC',
  indigo500: '#6366F1',
  indigo600: '#3448F0',
  indigo700: '#2839C9',
  indigo800: '#1E2A9B',
  emerald50: '#ECFDF5',
  emerald100: '#D1FAE5',
  emerald200: '#A7F3D0',
  emerald500: '#10B981',
  emerald600: '#059669',
  emerald700: '#047857',
  amber50: '#FFFBEB',
  amber200: '#FDE68A',
  amber800: '#92400E',
  amber900: '#78350F',
};

// ---------------------------------------------------------------------------
// Page chrome
// ---------------------------------------------------------------------------

function Logo(): React.JSX.Element {
  return (
    <Box
      component="a"
      href="/"
      sx={{display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none'}}
    >
      <Box component="img" src={logoMark} alt="GoodShowHQ" sx={{width: 28, height: 28, display: 'block'}} />
      <Typography
        component="span"
        sx={{fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em', color: C.slate900}}
      >
        GoodShowHQ
      </Typography>
    </Box>
  );
}

function Header(): React.JSX.Element {
  return (
    <Box component="header" sx={{borderBottom: `1px solid ${C.slate200}`, bgcolor: '#FFFFFF'}}>
      <Container maxWidth="md" sx={{py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <Logo />
        {/* Reference has an "Account" dropdown button here. We have
            no account-menu surface yet (Phase 4.2 is intentionally
            skipped) — fall back to a quiet text link to /profile. */}
        <Box
          component="a"
          href="/#/profile"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 14,
            fontWeight: 600,
            color: C.slate600,
            textDecoration: 'none',
            '&:hover': {color: C.slate900},
          }}
        >
          Skip to dashboard
        </Box>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Checklist item
// ---------------------------------------------------------------------------

type Status = 'pending' | 'active' | 'done';

interface ChecklistItemProps {
  index: number;
  status: Status;
  Icon: React.ComponentType<{sx?: object}>;
  title: string;
  estimate: string;
  expanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}

function ChecklistItem({
  index,
  status,
  Icon,
  title,
  estimate,
  expanded,
  onToggle,
  children,
}: ChecklistItemProps): React.JSX.Element {
  const isDone = status === 'done';
  const isActive = status === 'active' && !isDone;

  // Three visual treatments — done = emerald tint + strikethrough,
  // active = indigo border + ring + shadow, pending = neutral.
  const borderColor = isDone ? C.emerald200 : isActive ? C.indigo300 : C.slate200;
  const bgColor = isDone ? `${C.emerald50}80` : '#FFFFFF';
  const ringShadow = isActive ? `0 0 0 4px ${C.indigo100}, 0 1px 2px rgba(15,23,42,0.06)` : 'none';
  const indicatorBg = isDone ? C.emerald500 : isActive ? C.indigo600 : 'transparent';
  const indicatorColor = isDone || isActive ? '#FFFFFF' : C.slate400;
  const indicatorBorder = isDone || isActive ? 'none' : `2px solid ${C.slate300}`;
  const titleColor = isDone ? C.slate700 : C.slate900;
  const iconColor = isDone ? C.emerald600 : C.indigo600;

  return (
    <Box
      sx={{
        borderRadius: 4,
        border: `1px solid ${borderColor}`,
        bgcolor: bgColor,
        boxShadow: ringShadow,
        transition: 'box-shadow 200ms, border-color 200ms',
      }}
    >
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          gap: 2,
          p: 2.5,
          textAlign: 'left',
          cursor: 'pointer',
          // No expand affordance on done rows — but they're still
          // semantically buttons so reload preserves the visited
          // appearance on tab.
          '&:focus-visible': {outline: `2px solid ${C.indigo500}`, outlineOffset: 2},
        }}
      >
        {/* Status indicator (done check / active number / pending number) */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: indicatorBg,
            color: indicatorColor,
            border: indicatorBorder,
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {isDone ? <CheckCircleRounded sx={{fontSize: 20}} /> : index}
        </Box>

        {/* Title block */}
        <Box sx={{minWidth: 0, flex: 1}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Icon sx={{fontSize: 16, flexShrink: 0, color: iconColor}} />
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: titleColor,
                textDecoration: isDone ? 'line-through' : 'none',
              }}
            >
              {title}
            </Typography>
          </Box>
          <Typography sx={{mt: 0.25, fontSize: 12, color: C.slate500}}>{estimate}</Typography>
        </Box>

        {/* Chevron — hidden on done rows (no expand affordance) */}
        {!isDone && (
          <ExpandMoreRounded
            sx={{
              fontSize: 20,
              flexShrink: 0,
              color: C.slate400,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 200ms',
            }}
          />
        )}
      </Box>

      {expanded && !isDone && (
        <Box sx={{borderTop: `1px solid ${C.slate100}`, p: 2.5, pt: 2}}>{children}</Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step bodies
// ---------------------------------------------------------------------------

function PrincipleBullet({letter, children}: {letter: string; children: React.ReactNode}): React.JSX.Element {
  return (
    <Box component="li" sx={{display: 'flex', gap: 1.5}}>
      <Box
        sx={{
          mt: 0.25,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: '50%',
          bgcolor: C.indigo100,
          color: C.indigo700,
          fontSize: 10,
          fontWeight: 700,
        }}
      >
        {letter}
      </Box>
      <Typography sx={{fontSize: 14, lineHeight: 1.7, color: C.slate700}}>{children}</Typography>
    </Box>
  );
}

function DonePillButton({onClick, disabled, children}: {onClick: () => void; disabled?: boolean; children: React.ReactNode}): React.JSX.Element {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      sx={{
        borderRadius: 999,
        bgcolor: C.slate900,
        color: '#FFFFFF',
        px: 2,
        py: 1,
        fontSize: 14,
        fontWeight: 700,
        textTransform: 'none',
        '&:hover': {bgcolor: C.slate800},
        '&.Mui-disabled': {bgcolor: C.slate400, color: '#FFFFFF'},
      }}
    >
      {children}
    </Button>
  );
}

function ExternalLink({href, children}: {href: string; children: React.ReactNode}): React.JSX.Element {
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 14,
        fontWeight: 700,
        color: C.indigo600,
        textDecoration: 'none',
        '&:hover': {color: C.indigo800},
      }}
    >
      {children}
      <OpenInNewRounded sx={{fontSize: 14}} />
    </Box>
  );
}

interface StepBodyProps {
  onComplete: () => void;
  pending: boolean;
}

function PrinciplesBody({onComplete, pending}: StepBodyProps): React.JSX.Element {
  const navigate = useNavigate();
  return (
    <Box>
      <Typography sx={{fontSize: 14, lineHeight: 1.7, color: C.slate700}}>
        Two things matter before you set up:
      </Typography>
      <Stack component="ul" spacing={1.5} sx={{mt: 1.5, pl: 0, listStyle: 'none'}}>
        <PrincipleBullet letter="A">
          <Box component="strong" sx={{fontWeight: 700, color: C.slate900}}>
            Each kid needs their own Google account.
          </Box>{' '}
          Two kids on one account confuses YouTube's algorithm — neither kid gets their best feed.
        </PrincipleBullet>
        <PrincipleBullet letter="B">
          <Box component="strong" sx={{fontWeight: 700, color: C.slate900}}>
            Use regular YouTube, not YouTube Kids.
          </Box>{' '}
          YouTube Kids hides most of YouTube's actually educational content. GoodShowHQ does the safety filtering for you, so you keep access to the good stuff.
        </PrincipleBullet>
      </Stack>
      <Box sx={{mt: 2.5, display: 'flex', flexWrap: 'wrap', gap: 1}}>
        <Button
          onClick={() => navigate('/setup')}
          startIcon={<MenuBookRounded sx={{fontSize: 16}} />}
          sx={{
            borderRadius: 999,
            border: `1px solid ${C.slate200}`,
            bgcolor: '#FFFFFF',
            color: C.slate700,
            px: 2,
            py: 1,
            fontSize: 14,
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': {bgcolor: C.slate50},
          }}
        >
          Read the full guide
        </Button>
        <DonePillButton onClick={onComplete} disabled={!pending}>
          Got it ✓
        </DonePillButton>
      </Box>
    </Box>
  );
}

function GoogleAccountBody({onComplete, pending}: StepBodyProps): React.JSX.Element {
  return (
    <Box>
      <Typography sx={{fontSize: 14, lineHeight: 1.7, color: C.slate700}}>
        Each kid needs their own Google account to sign in to YouTube. You'll only need to do this once per kid.
      </Typography>
      <Stack spacing={1.5} sx={{mt: 2}}>
        <Box sx={{borderRadius: 3, border: `1px solid ${C.slate200}`, bgcolor: '#FFFFFF', p: 2}}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: C.slate500,
            }}
          >
            For kids under 13
          </Typography>
          <Typography sx={{mt: 0.5, fontSize: 14, lineHeight: 1.7, color: C.slate700}}>
            Use Google Family Link to set up a supervised account. You stay the parent on the supervision side — you control sign-in, app permissions, and screen time. About 5 minutes per kid.
          </Typography>
          {/* Family Link "supervised experience" gotcha. Google's
              flow defaults to a restrictive tier that hides most of
              what GoodShowHQ is meant to curate. Surfaced here as a
              compact sub-card so it's visible at the moment the
              parent clicks through to Family Link. */}
          <Box
            sx={{
              mt: 1.5,
              borderRadius: 2,
              border: `1px solid ${C.amber200}`,
              bgcolor: C.amber50,
              p: 1.5,
            }}
          >
            <Typography sx={{fontSize: 12, lineHeight: 1.7, color: C.amber900}}>
              <Box component="strong" sx={{fontWeight: 700, color: C.amber900}}>
                During setup, Google asks what YouTube your kid should see.
              </Box>{' '}
              Pick the most permissive option (usually labeled "Most of YouTube") — not "YouTube Kids" or the more restrictive supervised tier. GoodShowHQ does its own filtering on top.
            </Typography>
          </Box>
          <Box sx={{mt: 1.5}}>
            <ExternalLink href="https://families.google.com/familylink">
              Open Google Family Link
            </ExternalLink>
          </Box>
        </Box>
        <Box sx={{borderRadius: 3, border: `1px solid ${C.slate200}`, bgcolor: '#FFFFFF', p: 2}}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: C.slate500,
            }}
          >
            For kids 13 and up
          </Typography>
          <Typography sx={{mt: 0.5, fontSize: 14, lineHeight: 1.7, color: C.slate700}}>
            They can have their own personal Google account. Same idea — their Google identity, their YouTube feed.
          </Typography>
          <Box sx={{mt: 1.5}}>
            <ExternalLink href="https://accounts.google.com/signup">
              Create a Google account
            </ExternalLink>
          </Box>
        </Box>
      </Stack>
      <Box sx={{mt: 2.5}}>
        <DonePillButton onClick={onComplete} disabled={!pending}>
          Done — they have a Google account
        </DonePillButton>
      </Box>
    </Box>
  );
}

function DesktopBody({onComplete, pending}: StepBodyProps): React.JSX.Element {
  // The /get page already does device-aware download CTAs. Keeping
  // the macOS / Windows buttons inline matches the reference but
  // routing through /get gives the parent a richer install
  // experience if they need it.
  return (
    <Box>
      <Typography sx={{fontSize: 14, lineHeight: 1.7, color: C.slate700}}>
        GoodShowHQ Desktop runs in the background on your kid's computer. It watches YouTube activity on their account and sends data to GoodShowHQ's AI for review. Doesn't change how YouTube works for them.
      </Typography>
      <Box sx={{mt: 2, display: 'grid', gap: 1, gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}}}>
        <Button
          component="a"
          href="/#/get"
          startIcon={<AppleRounded sx={{fontSize: 22}} />}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
            borderRadius: 3,
            bgcolor: C.slate900,
            color: '#FFFFFF',
            px: 2,
            py: 1.5,
            textTransform: 'none',
            '&:hover': {bgcolor: C.slate800},
          }}
        >
          <Box sx={{textAlign: 'left'}}>
            <Typography sx={{fontSize: 10, lineHeight: 1, color: '#FFFFFF'}}>Download for</Typography>
            <Typography sx={{mt: 0.5, fontSize: 16, fontWeight: 700, lineHeight: 1, color: '#FFFFFF'}}>
              macOS
            </Typography>
          </Box>
        </Button>
        <Button
          component="a"
          href="/#/get"
          startIcon={<DesktopWindowsRounded sx={{fontSize: 22}} />}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
            borderRadius: 3,
            bgcolor: C.slate900,
            color: '#FFFFFF',
            px: 2,
            py: 1.5,
            textTransform: 'none',
            '&:hover': {bgcolor: C.slate800},
          }}
        >
          <Box sx={{textAlign: 'left'}}>
            <Typography sx={{fontSize: 10, lineHeight: 1, color: '#FFFFFF'}}>Download for</Typography>
            <Typography sx={{mt: 0.5, fontSize: 16, fontWeight: 700, lineHeight: 1, color: '#FFFFFF'}}>
              Windows
            </Typography>
          </Box>
        </Button>
      </Box>
      <Typography sx={{mt: 1.5, fontSize: 12, color: C.slate500}}>
        Install on the computer your kid actually uses for YouTube — not your own.
      </Typography>
      <Box sx={{mt: 2.5}}>
        <DonePillButton onClick={onComplete} disabled={!pending}>
          Done — Desktop is installed
        </DonePillButton>
      </Box>
    </Box>
  );
}

function YoutubeBody({onComplete, pending}: StepBodyProps): React.JSX.Element {
  return (
    <Box>
      <Typography sx={{fontSize: 14, lineHeight: 1.7, color: C.slate700}}>
        On your kid's computer, open GoodShowHQ Desktop and sign in to YouTube using{' '}
        <Box component="strong" sx={{fontWeight: 700, color: C.slate900}}>
          their
        </Box>{' '}
        Google account (the one you just set up). GoodShowHQ starts collecting within a minute.
      </Typography>
      <Box
        sx={{
          mt: 2,
          borderRadius: 3,
          border: `1px solid ${C.amber200}`,
          bgcolor: C.amber50,
          p: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: C.amber800,
          }}
        >
          One thing to double-check
        </Typography>
        <Typography sx={{mt: 0.5, fontSize: 14, lineHeight: 1.7, color: C.amber900}}>
          You're signing in to{' '}
          <Box component="strong" sx={{fontWeight: 700}}>
            regular YouTube
          </Box>
          , not YouTube Kids. GoodShowHQ does the safety filtering on top of regular YouTube — if you sign in to YouTube Kids, you'll miss most of the educational content this product is designed to surface.
        </Typography>
      </Box>
      <Box sx={{mt: 2.5}}>
        <DonePillButton onClick={onComplete} disabled={!pending}>
          Done — they're signed in
        </DonePillButton>
      </Box>
      <Typography sx={{mt: 1.5, fontSize: 12, color: C.slate400}}>
        We'll auto-detect when this is complete and move you to the dashboard.
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface ItemDef {
  key: WelcomeStep;
  // Maps the WelcomeStep enum to the matching boolean field on
  // WelcomeState — saves a switch on every render to compute
  // current done-status.
  doneField: keyof Pick<
    WelcomeState,
    'principlesAcknowledged' | 'googleAccountSetUp' | 'desktopInstalled' | 'kidSignedIn'
  >;
  Icon: React.ComponentType<{sx?: object}>;
  title: string;
  estimate: string;
  Body: React.ComponentType<StepBodyProps>;
}

const ITEMS: ItemDef[] = [
  {
    key: 'PRINCIPLES',
    doneField: 'principlesAcknowledged',
    Icon: MenuBookRounded,
    title: 'Read the two key principles',
    estimate: '2 min · how this works and why',
    Body: PrinciplesBody,
  },
  {
    key: 'GOOGLE_ACCOUNT',
    doneField: 'googleAccountSetUp',
    Icon: PeopleRounded,
    title: 'Set up a Google account for your first kid',
    estimate: '~5 min · external (Google)',
    Body: GoogleAccountBody,
  },
  {
    key: 'DESKTOP_INSTALLED',
    doneField: 'desktopInstalled',
    Icon: DesktopWindowsRounded,
    title: 'Install GoodShowHQ Desktop on their computer',
    estimate: '~3 min · download + install',
    Body: DesktopBody,
  },
  {
    key: 'KID_SIGNED_IN',
    doneField: 'kidSignedIn',
    Icon: YouTubeRounded,
    title: 'Sign your kid in to YouTube on Desktop',
    estimate: '~1 min · in the Desktop app',
    Body: YoutubeBody,
  },
];

const ZERO: WelcomeState = {
  principlesAcknowledged: false,
  googleAccountSetUp: false,
  desktopInstalled: false,
  kidSignedIn: false,
  completedAt: null,
  skippedAt: null,
};

interface WelcomeProps {
  client: ApolloClient<NormalizedCacheObject>;
}

export default function Welcome({client}: WelcomeProps): React.JSX.Element {
  const navigate = useNavigate();
  const [state, setState] = useState<WelcomeState>(ZERO);
  const [hydrated, setHydrated] = useState(false);
  const [expanded, setExpanded] = useState<WelcomeStep | null>(null);

  const {mutateAsync: fetchBundle} = useGetWelcomeBundle(client);
  const {mutateAsync: markStep} = useMarkWelcomeStep(client);
  const {mutateAsync: skipFlow} = useSkipWelcome(client);

  // Hydrate from server on mount. Run-once via the boolean guard.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const bundle = await fetchBundle();
        if (cancelled) return;
        const initial = bundle.welcomeState ?? ZERO;
        setState(initial);
        const firstPending = ITEMS.find(i => !initial[i.doneField]);
        setExpanded(firstPending?.key ?? null);
      } catch (err) {
        console.error('[Welcome] failed to hydrate welcomeState:', err);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // fetchBundle is a stable react-query callback; deps left empty
    // intentionally so this only runs on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completedCount = useMemo(
    () => ITEMS.filter(i => state[i.doneField]).length,
    [state],
  );
  const allDone = completedCount === ITEMS.length;

  // Auto-redirect to /profile once all four steps are done.
  useEffect(() => {
    if (!allDone) return;
    const t = setTimeout(() => navigate('/profile', {replace: true}), 1200);
    return () => clearTimeout(t);
  }, [allDone, navigate]);

  // Computed status per item — only the first incomplete one is
  // 'active'; everything before it is 'done', everything after is
  // 'pending'.
  function statusFor(item: ItemDef): Status {
    if (state[item.doneField]) return 'done';
    const firstPending = ITEMS.find(i => !state[i.doneField]);
    return item.key === firstPending?.key ? 'active' : 'pending';
  }

  async function handleComplete(step: WelcomeStep) {
    // Optimistic — flip the matching boolean locally, fire the
    // mutation, and on the response replace local state with the
    // canonical server snapshot (which also stamps `completedAt`).
    const item = ITEMS.find(i => i.key === step);
    if (!item) return;
    const optimistic: WelcomeState = {...state, [item.doneField]: true};
    setState(optimistic);
    const nextPending = ITEMS.find(i => !optimistic[i.doneField]);
    setExpanded(nextPending?.key ?? null);
    try {
      const persisted = await markStep(step);
      setState(persisted);
    } catch (err) {
      console.error(`[Welcome] markWelcomeStep(${step}) failed:`, err);
      // Revert on error so the parent can retry.
      setState(state);
    }
  }

  async function handleSkip(e: React.MouseEvent) {
    e.preventDefault();
    try {
      await skipFlow();
    } catch (err) {
      console.error('[Welcome] skipWelcome failed:', err);
    } finally {
      navigate('/profile', {replace: true});
    }
  }

  function toggleExpanded(key: WelcomeStep) {
    // Done rows aren't toggleable — guarded at the click handler
    // level so the affordance disappears cleanly with no chevron.
    if (state[ITEMS.find(i => i.key === key)!.doneField]) return;
    setExpanded(prev => (prev === key ? null : key));
  }

  // Show a small spinner during the initial fetch — most users
  // hit this page sub-second so the spinner barely flashes; we
  // need it for the "returning parent mid-flow" case where
  // hydration changes which step is active.
  if (!hydrated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: C.slate50,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={32} sx={{color: C.indigo600}} />
      </Box>
    );
  }

  return (
    <Box sx={{minHeight: '100vh', bgcolor: C.slate50, color: C.slate900}}>
      <Header />
      <Container component="main" maxWidth="md" sx={{px: 3, py: {xs: 5, md: 8}}}>
        {/* Hero */}
        <Box>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              borderRadius: 999,
              bgcolor: C.indigo100,
              color: C.indigo700,
              px: 1.5,
              py: 0.5,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <AutoAwesomeRounded sx={{fontSize: 14}} />
            Welcome to GoodShowHQ
          </Box>
          <Typography
            component="h1"
            sx={{
              mt: 2,
              fontSize: {xs: 28, md: 36},
              fontWeight: 800,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              color: C.slate900,
            }}
          >
            Let's set up your first kid.
          </Typography>
          <Typography sx={{mt: 1.5, maxWidth: 640, fontSize: 16, lineHeight: 1.7, color: C.slate600}}>
            Four steps. About 15 minutes total — most of it is on Google's side. We'll auto-detect each one as you go.
          </Typography>
        </Box>

        {/* Progress bar — slate-200 track / indigo-600 fill, 300ms */}
        <Box sx={{mt: 3.5}}>
          <Box sx={{mb: 1, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between'}}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: C.slate500,
              }}
            >
              Setup progress
            </Typography>
            <Typography sx={{fontSize: 12, fontWeight: 600, color: C.slate700}}>
              {completedCount} of {ITEMS.length}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(completedCount / ITEMS.length) * 100}
            sx={{
              height: 6,
              borderRadius: 999,
              bgcolor: C.slate200,
              '& .MuiLinearProgress-bar': {
                bgcolor: C.indigo600,
                borderRadius: 999,
                transition: 'transform 300ms',
              },
            }}
          />
        </Box>

        {/* Checklist */}
        <Stack spacing={1.5} sx={{mt: 3.5}}>
          {ITEMS.map((item, i) => {
            const status = statusFor(item);
            const Body = item.Body;
            return (
              <ChecklistItem
                key={item.key}
                index={i + 1}
                status={status}
                Icon={item.Icon}
                title={item.title}
                estimate={item.estimate}
                expanded={expanded === item.key && status !== 'done'}
                onToggle={() => toggleExpanded(item.key)}
              >
                <Body
                  onComplete={() => handleComplete(item.key)}
                  pending={status !== 'done'}
                />
              </ChecklistItem>
            );
          })}
        </Stack>

        {/* All-done card → auto-redirects after 1.2s */}
        {allDone && (
          <Box
            sx={{
              mt: 4,
              borderRadius: 4,
              background: `linear-gradient(135deg, ${C.emerald500}, ${C.emerald700})`,
              color: '#FFFFFF',
              boxShadow: '0 10px 24px rgba(16,185,129,0.18)',
              p: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                mx: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(4px)',
              }}
            >
              <CheckCircleRounded sx={{fontSize: 24}} />
            </Box>
            <Typography sx={{mt: 1.5, fontSize: 20, fontWeight: 800}}>All set up!</Typography>
            <Typography sx={{mt: 0.5, fontSize: 14, color: C.emerald100}}>
              Taking you to your kid's dashboard…
            </Typography>
          </Box>
        )}

        {/* Skip footer — hidden in the all-done state */}
        {!allDone && (
          <Box
            sx={{mt: 4, borderTop: `1px solid ${C.slate200}`, pt: 3, textAlign: 'center'}}
          >
            <Box
              component="a"
              href="/#/profile"
              onClick={handleSkip}
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: C.slate500,
                textDecoration: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': {color: C.slate900},
              }}
            >
              Skip — I'll figure it out
              <ArrowForwardRounded sx={{fontSize: 14}} />
            </Box>
            <Typography sx={{mt: 0.75, fontSize: 12, color: C.slate400}}>
              You can always come back to this guide from Settings.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
